// Data model for webinars, registrations and email deliveries, shared by both backends:
// the IndexedDB prototype and Supabase. Field names match the Supabase tables in camelCase.

import type { RegistrationErrors, RegistrationInput } from "./shared/validation";

export const WEBINAR_STATUSES = ["draft", "published", "registration_closed", "completed", "cancelled"] as const;
export type WebinarStatus = typeof WEBINAR_STATUSES[number];

export const WEBINAR_STATUS_LABELS: Record<WebinarStatus, string> = {
  draft: "Draft",
  published: "Published",
  registration_closed: "Registration Closed",
  completed: "Completed",
  cancelled: "Cancelled",
};

/**
 * Statuses that move a webinar into the dashboard's Archive. Setting an archived webinar back to
 * draft or published starts a new round: its current registrations are archived (see archivedAt),
 * so the seat count restarts. The database does this in a trigger; the prototype mirrors it.
 */
export const ARCHIVED_WEBINAR_STATUSES: readonly WebinarStatus[] = ["registration_closed", "completed", "cancelled"];

export const isArchivedStatus = (status: WebinarStatus) => ARCHIVED_WEBINAR_STATUSES.includes(status);

/** True when saving this status change archives the webinar's current registrations */
export const reopensWebinar = (from: WebinarStatus, to: WebinarStatus) => isArchivedStatus(from) && !isArchivedStatus(to);

export const REGISTRATION_STATUSES = ["registered", "cancelled", "attended"] as const;
export type RegistrationStatus = typeof REGISTRATION_STATUSES[number];

export const REGISTRATION_STATUS_LABELS: Record<RegistrationStatus, string> = {
  registered: "Registered",
  cancelled: "Cancelled",
  attended: "Attended",
};

/** Added and edited by administrators in the dashboard (/admin) */
export interface Webinar {
  id: string;
  title: string;
  slug: string;
  /** One or two sentences for cards */
  summary: string;
  description: string;
  topics: string[];
  presenter?: string;
  presenterRole?: string;
  /** Key of a photo in photos.ts. Without one, the presenter's initials are shown */
  presenterPhoto?: string;
  /** ISO 8601 */
  startAt: string;
  endAt: string;
  /** When empty, registration closes when the webinar starts */
  registrationDeadline?: string;
  /** Maximum attendees. Empty means no limit */
  capacity?: number;
  meetingPlatform?: string;
  /** Shown on past events when provided */
  recordingUrl?: string;
  resourceUrl?: string;
  status: WebinarStatus;
  /** Prototype demonstration content, labelled wherever it appears. Never true for real events */
  isDemo: boolean;
}

/** What the dashboard's webinar form saves. The ID is assigned when a webinar is added */
export type WebinarInput = Omit<Webinar, "id" | "isDemo">;

export interface Registration {
  id: string;
  /** Human-friendly reference shown to the attendee, e.g. KMW-00012 */
  reference: string;
  webinarId: string;
  firstName: string;
  lastName: string;
  /** Normalised: trimmed and lower-case */
  email: string;
  phone: string;
  organisation?: string;
  professionalRole?: string;
  status: RegistrationStatus;
  privacyNoticeVersion: string;
  /** Optional, separate from registration emails */
  marketingConsent: boolean;
  /** Set when the administrator records joining instructions sent by hand (WhatsApp or their own email) */
  instructionsSentAt?: string;
  /**
   * Set when the webinar was reopened for a new round. Archived registrations belong to an earlier
   * round: they don't take seats, the person can register again, and they're never emailed.
   */
  archivedAt?: string;
  /** For archived registrations: when their round started, before the webinar was reopened */
  roundStartAt?: string;
  registeredAt: string;
  updatedAt: string;
}

export type NewRegistration = RegistrationInput;

export interface RegistrationUpdate {
  status?: RegistrationStatus;
  /** An ISO timestamp records a manual send; null clears the record */
  instructionsSentAt?: string | null;
}

export interface RegistrationFilters {
  /** Matches first name, surname, full name, email or reference */
  search?: string;
  webinarId?: string;
  status?: RegistrationStatus;
  /** current (the default): this round's registrations · archived: earlier rounds · all */
  round?: "current" | "archived" | "all";
  /** Only registrations with a failed or bounced email. Applied in the dashboard, not by the repository */
  emailIssues?: boolean;
  /** By registration date. Defaults to newest first */
  sort?: "newest" | "oldest";
}

// ══════════════════════════════════════════════════════════════════════════════
//   EMAIL
// ══════════════════════════════════════════════════════════════════════════════

export type EmailType = "confirmation" | "joining_instructions";

export const EMAIL_TYPE_LABELS: Record<EmailType, string> = {
  confirmation: "Confirmation",
  joining_instructions: "Joining instructions",
};

/**
 * sent = accepted by the email provider (Resend), which is not proof it reached the inbox.
 * simulated = the prototype, which never sends real email.
 */
export type EmailStatus = "queued" | "sending" | "sent" | "delivered" | "failed" | "bounced" | "simulated";

export const EMAIL_STATUS_LABELS: Record<EmailStatus, string> = {
  queued: "Queued",
  sending: "Sending",
  sent: "Sent",
  delivered: "Delivered",
  failed: "Failed",
  bounced: "Bounced",
  simulated: "Simulated",
};

export interface EmailDelivery {
  id: string;
  registrationId: string;
  emailType: EmailType;
  status: EmailStatus;
  attemptCount: number;
  sendRound: number;
  /** Sanitised: never contains email addresses. For a bounce, Resend's reason */
  lastError?: string;
  sentAt?: string;
  /** Reported by Resend's webhook once the recipient's mail server accepted it */
  deliveredAt?: string;
  /** The recipient marked it as spam (Resend webhook); the status stays as it was */
  complainedAt?: string;
  updatedAt: string;
}

/** What the server says after a registration is saved */
export interface RegistrationResult {
  reference: string;
  registeredAt: string;
  /** sent: handed to the email provider · queued: will be sent shortly · failed: will be retried · simulated: prototype */
  confirmationEmail: "sent" | "queued" | "failed" | "simulated";
}

/** Kept in this browser tab so the confirmation page survives a refresh */
export interface RegistrationReceipt extends RegistrationResult {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  webinar: Pick<Webinar, "id" | "slug" | "title" | "startAt" | "endAt" | "meetingPlatform">;
}

export interface SendRequest {
  webinarId: string;
  /** Leave out to send to everyone eligible */
  registrationIds?: string[];
  /** Send again to people who already received joining instructions */
  resend?: boolean;
}

/**
 * Where the email limits stand. Resend's free plan allows 100 emails a day (per UTC day, so it
 * resets at 00:00 UTC = 02:00 SAST) and 3,000 a month. Confirmations and joining instructions share both.
 */
export interface EmailQuota {
  /** null: no daily limit */
  dailyLimit: number | null;
  /** Sent today: the higher of our own count and Resend's */
  used: number;
  /** null when there's no daily limit */
  dayRemaining: number | null;
  /** ISO: the next 00:00 UTC */
  resetsAt: string;
  /** Resend refused an email today because the daily quota was used up */
  exhausted: boolean;
  /** null: no monthly limit */
  monthlyLimit: number | null;
  monthUsed: number;
  monthRemaining: number | null;
  /** ISO: the start of the next monthly period, 00:00 UTC */
  monthResetsAt: string;
  monthExhausted: boolean;
  /** Being sent right now */
  inFlight: number;
  /** How many more can go out now, under the tighter of the two limits; null when there are none */
  remaining: number | null;
  /** Queued emails, including those held back by a limit */
  waiting: number;
}

/** Which limit is stopping (or will stop) sending first: the month only when it's the tighter one */
export const bindingLimit = (q: EmailQuota): "day" | "month" =>
  q.monthRemaining !== null && (q.dayRemaining === null || q.monthRemaining <= q.dayRemaining) ? "month" : "day";

/** When sending can start again once the binding limit is reached */
export const quotaResumesAt = (q: EmailQuota) => (bindingLimit(q) === "month" ? q.monthResetsAt : q.resetsAt);

export interface SendProgress {
  /** Emails added to the queue by this request */
  queued: number;
  sent: number;
  failed: number;
  /** Still waiting in the queue */
  remaining: number;
  /** False when the server has no email provider configured yet */
  emailEnabled: boolean;
  /** Where today's limit stands after this send */
  quota?: EmailQuota;
}

// ══════════════════════════════════════════════════════════════════════════════
//   SUPABASE PLAN USAGE
// ══════════════════════════════════════════════════════════════════════════════

export type UsageMetric = "database" | "storage" | "egress" | "mau" | "log_ingestion" | "log_query";

/** One line of the plan-usage panel */
export interface UsageItem {
  metric: UsageMetric;
  label: string;
  /** null until the first reading: hand-entered metrics start empty */
  used: number | null;
  limit: number;
  unit: "bytes" | "users";
  /** measured: by the database, when the dashboard opens · manual: copied from Supabase's usage page */
  source: "measured" | "manual";
  recordedAt?: string;
  /** The oldest reading of the last 30 days, to show growth */
  previous?: { value: number; recordedAt: string };
}

export interface AdminSession {
  email: string;
  isAdmin: boolean;
}

/** One entry of the admin audit log (production only) */
export interface AdminActivity {
  id: number;
  occurredAt: string;
  /** null for visitors and the system */
  actorEmail: string | null;
  /** e.g. webinar.updated, joining_link.saved, emails.send_joining, rate_limit.registration */
  action: string;
  outcome: "ok" | "blocked" | "denied";
  webinarId: string | null;
  details: Record<string, unknown>;
}

// ══════════════════════════════════════════════════════════════════════════════
//   ERRORS
// ══════════════════════════════════════════════════════════════════════════════

export type RegistrationErrorCode = "duplicate" | "full" | "closed" | "not_found" | "invalid" | "rate_limited" | "unavailable";

/** Thrown by register(); `code` picks the message, `fieldErrors` marks fields */
export class RegistrationError extends Error {
  code: RegistrationErrorCode;
  fieldErrors?: RegistrationErrors;
  /** rate_limited: seconds until the server accepts another attempt */
  retryAfter?: number;
  constructor(code: RegistrationErrorCode, message: string, fieldErrors?: RegistrationErrors, retryAfter?: number) {
    super(message);
    this.name = "RegistrationError";
    this.code = code;
    this.fieldErrors = fieldErrors;
    this.retryAfter = retryAfter;
  }
}

export type AdminErrorCode = "not_signed_in" | "not_admin" | "sign_in_failed" | "rate_limited"
  | "no_meeting_link" | "webinar_cancelled" | "duplicate" | "invalid" | "unavailable";

export class AdminError extends Error {
  code: AdminErrorCode;
  /** rate_limited: seconds to wait, when the server says */
  retryAfter?: number;
  constructor(code: AdminErrorCode, message: string, retryAfter?: number) {
    super(message);
    this.name = "AdminError";
    this.code = code;
    this.retryAfter = retryAfter;
  }
}

// Checked by name rather than instanceof, which can break when classes extending Error are transpiled
export const isRegistrationError = (e: unknown): e is RegistrationError =>
  e instanceof Error && e.name === "RegistrationError";

export const isAdminError = (e: unknown): e is AdminError =>
  e instanceof Error && e.name === "AdminError";
